<script setup lang="ts">
import { computed, onMounted, watch, type PropType } from 'vue';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxTagBox } from 'devextreme-vue/tag-box';
import type { LookupStore } from '../composables/useLookupStore';

const props = defineProps({
  modelValue: { type: [String, Number, Array, null] as unknown as any, default: null },
  lookup: { type: Object as PropType<LookupStore<any>>, required: true },
  placeholder: { type: String, default: 'Выберите…' },
  disabled: { type: Boolean, default: false },
  searchEnabled: { type: Boolean, default: true },
  /** When true, allow null/clear. */
  showClearButton: { type: Boolean, default: true },
  /** Multi-select mode (value is an array of ids). */
  multiple: { type: Boolean, default: false },
  /** Optional filter over lookup items. */
  itemsFilter: {
    type: Function as unknown as PropType<((item: any) => boolean) | null>,
    default: null,
  },
  /** Extra key to remount when dependent form values change. */
  reloadKey: { type: [String, Number], default: '' },
});

const emit = defineEmits<{
  (e: 'update:modelValue', v: number | string | null | Array<number | string>): void;
}>();

onMounted(() => { void props.lookup.ensureLoaded(); });

const dataSource = computed(() => {
  const items = props.lookup.items.value;
  if (!props.itemsFilter) return items;
  return items.filter((item) => props.itemsFilter!(item));
});
/** DevExtreme SelectBox does not always react to async dataSource updates. */
const dataSourceKey = computed(
  () =>
    `${props.lookup.loaded.value}:${props.lookup.items.value.length}:${dataSource.value.length}:${props.multiple ? 'm' : 's'}:${props.reloadKey}`,
);
const valueExpr = computed(() => String(props.lookup.options.idField));
const displayExpr = computed(() => {
  if (props.lookup.options.format) {
    return (item: any) => (item ? props.lookup.options.format!(item) : '');
  }
  return String(props.lookup.options.labelField);
});

const noDataText = computed(() => {
  if (props.lookup.loading.value) return 'Загрузка…';
  if (props.lookup.error.value) return 'Ошибка загрузки';
  return 'Нет данных';
});

function onChange(value: any) { emit('update:modelValue', value); }

watch(
  () => props.lookup.error.value,
  (err) => {
    if (err) console.error('[LookupBox] load failed:', err);
  },
);
</script>

<template>
  <DxTagBox
    v-if="multiple"
    :key="dataSourceKey"
    :value="Array.isArray(modelValue) ? modelValue : []"
    :data-source="dataSource"
    :value-expr="valueExpr"
    :key-expr="valueExpr"
    :display-expr="(displayExpr as any)"
    :placeholder="placeholder"
    :disabled="disabled"
    :search-enabled="searchEnabled"
    :show-clear-button="showClearButton"
    :no-data-text="noDataText"
    :show-selection-controls="true"
    apply-value-mode="useButtons"
    @value-changed="(e: any) => onChange(e.value ?? [])"
  />
  <DxSelectBox
    v-else
    :key="dataSourceKey"
    :value="modelValue"
    :data-source="dataSource"
    :value-expr="valueExpr"
    :key-expr="valueExpr"
    :display-expr="(displayExpr as any)"
    :placeholder="placeholder"
    :disabled="disabled"
    :search-enabled="searchEnabled"
    :show-clear-button="showClearButton"
    :no-data-text="noDataText"
    @value-changed="(e: any) => onChange(e.value)"
  />
</template>
